import React, {
    forwardRef,
    useCallback,
    useEffect,
    useState,
    useRef,
    useImperativeHandle,
    useMemo,
} from 'react';
import classNames from 'classnames';
import { Text, useClasses, createUseStyles, Advice, Avatar, useTheme } from '@Sage/hoi-poi-ui';
import ScrollBar from 'react-custom-scrollbars';

import styles from './styles';

const useStyles = createUseStyles(styles, 'MentionList');

const MENTION_LIST_ITEM_HEIGHT = 33;
const MAX_MENTION_LIST_ITEMS_VISIBLE_WITHOUT_SCROLL = 8;

const MentionList = forwardRef(
    (
        {
            texts,
            command,
            classes: classesProp,
            items = [],
            maxVisibleItems = MAX_MENTION_LIST_ITEMS_VISIBLE_WITHOUT_SCROLL,
            hideNoResultsPopover,
        },
        ref,
    ) => {
        const classes = useClasses(useStyles, classesProp);
        const [selectedIndex, setSelectedIndex] = useState(0);
        const scrollBarRef = useRef();
        const theme = useTheme();

        useEffect(() => setSelectedIndex(0), [items]);

        const selectItem = useCallback(
            (index) => {
                const item = items[index];
                if (item) {
                    command({ id: item.id, label: item.name });
                }
            },
            [command, items],
        );

        const scrollHandler = useCallback((index) => {
            scrollBarRef?.current?.scrollTop(index * MENTION_LIST_ITEM_HEIGHT);
        }, []);

        const upHandler = useCallback(() => {
            const newIndex = (selectedIndex + items.length - 1) % items.length;
            setSelectedIndex(newIndex);
            scrollHandler(newIndex);
        }, [items, selectedIndex, scrollHandler]);

        const downHandler = useCallback(() => {
            const newIndex = (selectedIndex + 1) % items.length;
            setSelectedIndex(newIndex);
            scrollHandler(newIndex);
        }, [items, selectedIndex, scrollHandler]);

        const enterHandler = useCallback(() => {
            selectItem(selectedIndex);
        }, [selectItem, selectedIndex]);

        useImperativeHandle(ref, () => ({
            onKeyDown: ({ event }) => {
                switch (event.key) {
                    case 'ArrowUp':
                        upHandler();
                        return true;
                    case 'ArrowDown':
                        downHandler();
                        return true;
                    case 'Enter':
                    case 'Tab':
                        enterHandler();
                        return true;
                    default:
                        return false;
                }
            },
        }));

        const scrollBarProps = useMemo(
            () => ({
                autoHeight: true,
                autoHeightMax: MENTION_LIST_ITEM_HEIGHT * maxVisibleItems,
                autoHide: false,
                ref: scrollBarRef,
            }),
            [maxVisibleItems],
        );

        return (
            <div
                className={classNames(classes.root, {
                    [classes.isEmpty]: items.length === 0 && hideNoResultsPopover,
                })}
            >
                {texts?.advice && (
                    <Advice type="info" showIcon showCollapse={false} className={classes.advice}>
                        <Text type="caption" color={theme.colors.semantic.info600}>
                            {texts.advice}
                        </Text>
                    </Advice>
                )}
                {items.length ? (
                    <ScrollBar {...scrollBarProps}>
                        {items.map((item, index) => (
                            <div
                                className={classNames(classes.item, {
                                    [classes.selected]: index === selectedIndex,
                                })}
                                key={`suggestion-${item.id}`}
                                onClick={() => selectItem(index)}
                            >
                                <Avatar
                                    src={item.avatar.src}
                                    placeholder={item.avatar.placeholder}
                                    className={classes.avatar}
                                />
                                <Text
                                    color={theme.colors.textLight.primary}
                                    type="body"
                                    className={classes.name}
                                    isTruncated
                                >
                                    {item.name}
                                </Text>
                            </div>
                        ))}
                    </ScrollBar>
                ) : (
                    <Text>{texts?.noResults || 'No Results'}</Text>
                )}
            </div>
        );
    },
);

export default MentionList;
